import type { ShorthandValue, TileImageProps } from '@archipro-design/aria';
import {
    ArchizeenTile,
    FileTile,
    ProductTile,
    ProfessionalTile,
    ProjectTile,
    Box,
} from '@archipro-design/aria';
import type { TileSizes } from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import type { DetailedPinWithChecked } from '../component/all-pins/AllPins';
import { TileTopAction } from '../component/shared/TileTopAction';
import { DesignBoardPinTargetType } from 'generated/graphql';
import { UPLOAD_PLACEHOLDER_BG_IMG } from '~/modules/design-board/config';
import {
    ARCHIZEEN_TILE_WIDTH_MOBILE,
    PRODUCT_TILE_HEIGHT_MOBILE,
    PRODUCT_TILE_WIDTH_MOBILE,
    PROFESSIONAL_TILE_HEIGHT_DESKTOP,
    PROFESSIONAL_TILE_HEIGHT_MOBILE,
    PROJECT_TILE_HEIGHT_DESKTOP,
    PROJECT_TILE_HEIGHT_MOBILE,
    TILE_HEIGHT_DESKTOP,
    TILE_HEIGHT_MOBILE,
    TILE_WIDTH_DESKTOP,
    TILE_WIDTH_MOBILE,
} from '~/modules/design-board/config/css-styles-and-numbers';
import type {
    ArchizeenTileData,
    ProductTileData,
    ProfessionalTileData,
    ProjectTileData,
} from '~/modules/design-board/type';
import { ToggleBottomMenuButton } from '~/modules/design-board/component/mobile-menu/ToggleBottomMenuButton';
import { PlayFill } from '@archipro-design/icons';
import ShoppingAvailable from '@modules/tile/component/product-tile/ShoppingAvailable';
import { decodeEntities } from '~/utils';
import VariantDot from '~/modules/tile/component/product-tile/VariantDot';

export const getPinTile = ({
    assetsBaseUrl,
    pin,
    isMobile = true,
    isChecked,
    onUpdate,
    onDelete,
    index,
    isAllPins,
}: {
    assetsBaseUrl: string;
    pin: DetailedPinWithChecked;
    index: number;
    isMobile?: boolean;
    isChecked: boolean;
    onUpdate: (id: number, checked: boolean) => void;
    onDelete: (id: number) => void;
    isAllPins?: boolean;
}): React.ReactElement => {
    const defaultTileWidth = isMobile ? TILE_WIDTH_MOBILE : TILE_WIDTH_DESKTOP;
    const defaultTileHeight = isMobile
        ? TILE_HEIGHT_MOBILE
        : TILE_HEIGHT_DESKTOP;
    const defaultTileVariables = {
        height: defaultTileHeight,
        width: defaultTileWidth,
        dynamicSizingWidth: defaultTileWidth,
    };
    const defaultTileProps = {
        as: Link,
        to: pin.Link || '',
        size: 'dynamic' as Extract<TileSizes, 'dynamic'>,
    };

    const deletePin = (id: number) => {
        onDelete(id);
    };

    const isArticleTile = isPinTileType<ArchizeenTileData>(
        pin.TileData,
        DesignBoardPinTargetType.Article
    );

    const topActions = (
        <TileTopAction
            //Implent something for hiding share if type is a file/image
            isChecked={isChecked}
            id={pin.ID}
            onUpdate={onUpdate}
            deletePin={(id) => deletePin(id)}
            isAllPins={isAllPins}
            isArticleTile={isArticleTile}
            professionalLink={getPinWebsiteLink(pin)}
            pin={pin}
        />
    );

    const pinTitle = decodeEntities(pin.Title);

    if (
        isPinTileType<ProductTileData>(
            pin.TileData,
            DesignBoardPinTargetType.Product
        )
    ) {
        return (
            <ProductTile
                className={'pin-tile product-pin'}
                key={`${pin.ID}-${index}`}
                {...pin.TileData}
                {...defaultTileProps}
                productName={pinTitle}
                variables={{
                    height: isMobile
                        ? PRODUCT_TILE_HEIGHT_MOBILE
                        : TILE_HEIGHT_DESKTOP,
                    width: isMobile
                        ? PRODUCT_TILE_WIDTH_MOBILE
                        : TILE_WIDTH_DESKTOP,
                    dynamicSizingWidth: isMobile
                        ? PRODUCT_TILE_WIDTH_MOBILE
                        : TILE_WIDTH_DESKTOP,
                }}
                professionalName={pin.TileData.professionalName}
                variant={isMobile ? '02' : '01'}
                newDesignEnabled={true}
                topActions={topActions}
                topContent={
                    pin.TileData.canPurchase && !isChecked
                        ? [
                              <ShoppingAvailable
                                  key="shopping-available"
                                  floating={'left'}
                              />,
                          ]
                        : []
                }
                variants={pin.TileData.variants?.map(
                    ({ ID, Code, Label, Dot }) => {
                        return {
                            key: `variant-dot-${ID}`,
                            children: (
                                <VariantDot
                                    size={isMobile ? 9 : 13}
                                    colorCode={Code ?? ''}
                                    description={Label}
                                    image={
                                        Dot?.[isMobile ? 'Mobile' : 'Desktop']
                                    }
                                />
                            ),
                        };
                    }
                )}
            />
        );
    }

    if (
        isPinTileType<ProfessionalTileData>(
            pin.TileData,
            DesignBoardPinTargetType.Professional
        )
    ) {
        return (
            <ProfessionalTile
                className={'pin-tile professional-pin'}
                key={`${pin.ID}-${index}`}
                {...pin.TileData}
                {...defaultTileProps}
                location={undefined}
                services={undefined}
                title={pinTitle}
                variant={'04'}
                variables={{
                    ...defaultTileVariables,
                    height: isMobile
                        ? PROFESSIONAL_TILE_HEIGHT_MOBILE
                        : PROFESSIONAL_TILE_HEIGHT_DESKTOP,
                }}
                topActions={topActions}
            />
        );
    }

    if (
        isPinTileType<ProjectTileData>(
            pin.TileData,
            DesignBoardPinTargetType.Project
        )
    ) {
        return (
            <ProjectTile
                className={'pin-tile project-pin'}
                key={`${pin.ID}-${index}`}
                {...pin.TileData}
                {...defaultTileProps}
                professionalName={pin.TileData.professionalName}
                projectName={pinTitle}
                variant={isMobile ? '02' : '01'}
                newDesignEnabled={true}
                variables={{
                    ...defaultTileVariables,
                    height: isMobile
                        ? PROJECT_TILE_HEIGHT_MOBILE
                        : PROJECT_TILE_HEIGHT_DESKTOP,
                }}
                topActions={topActions}
            />
        );
    }

    if (
        isPinTileType<ArchizeenTileData>(
            pin.TileData,
            DesignBoardPinTargetType.Article
        )
    ) {
        const hasVideos =
            pin.Target.__typename === 'Article' && pin.Target.HasVideos;

        return (
            <ArchizeenTile
                className={`pin-tile article-pin ${
                    index % 2 === 0 ? 'even' : 'odd'
                }`}
                key={`${pin.ID}-${index}`}
                {...pin.TileData}
                {...defaultTileProps}
                variables={{
                    ...defaultTileVariables,
                    width: isMobile
                        ? ARCHIZEEN_TILE_WIDTH_MOBILE
                        : TILE_WIDTH_DESKTOP,
                }}
                image={{
                    src: pin.TileData.image[0] || '',
                    overlay: hasVideos
                        ? {
                              alwaysVisible: true,
                              icon: <PlayFill />,
                              variables: {
                                  backgroundColor: `rgba(0, 0, 0, 0.2)`,
                              },
                          }
                        : undefined,
                    width: `100%`,
                    height: `100%`,
                    alt: '',
                }}
                size={isMobile ? 'medium' : 'dynamic'}
                variant={isMobile ? '04' : '02'}
                topActions={topActions}
                extraActions={
                    isMobile
                        ? [
                              <Box key={`${pin.ID}-${index}`}>
                                  <ToggleBottomMenuButton
                                      bottomMenuData={{ type: 'pin' }}
                                      currentPinData={{
                                          ...pin,
                                          TileData: pin.TileData,
                                      }}
                                  />
                              </Box>,
                          ]
                        : undefined
                }
                title={{
                    maximumLines: isMobile ? 3 : 2,
                    children: pinTitle,
                }}
            />
        );
    }

    if (pin.Target.__typename === 'LibraryImage') {
        let link = pin.Target.LibraryItem?.Link;

        if (link && pin.URLSearchParams) {
            link = link.includes('?')
                ? `${link}&${pin.URLSearchParams}`
                : `${link}?${pin.URLSearchParams}`;
        }

        return (
            <FileTile
                className={'pin-tile photo-pin'}
                key={`${pin.ID}-${index}`}
                description={pin.Title}
                variant={'01'}
                size={'medium'}
                tileImage={getUploadFileTileImage(
                    pin.Target.LibraryImageFile?.PhotoTile || pin.PhotoTile,
                    pin.Title
                )}
                variables={defaultTileVariables}
                topActions={topActions}
                {...(link ? { as: Link, to: link } : { to: '' })}
            />
        );
    }

    if (pin.Target.__typename === 'MemberUpload') {
        const filename = pin.Target.FileName || '';
        const fileExtension = filename.split('.').pop();
        const isImage = fileExtension?.match(/(jpg|jpeg|png|gif|webp)$/i);

        const fileThumb = getUploadFileTileImage(
            `${assetsBaseUrl}${UPLOAD_PLACEHOLDER_BG_IMG}`,
            pin.Title,
            'contain'
        );
        const tileImage = isImage
            ? getUploadFileTileImage(pin.PhotoTile, pin.Title)
            : fileThumb;

        // TODO: force download for uploaded images for now

        return (
            <FileTile
                className={`pin-tile ${
                    isImage ? 'upload-image-pin' : 'upload-file-pin'
                }`}
                key={`${pin.ID}-${index}`}
                as="a"
                href={pin.Link}
                target={'_blank'}
                download={filename}
                description={pin.Title}
                variant={isImage ? '01' : '02'}
                iconText={fileExtension}
                size={'medium'}
                tileImage={tileImage}
                variables={defaultTileVariables}
                topActions={topActions}
            />
        );
    }

    return <div>Wrong pin type</div>;
};

export const isPinTileType = <
    T extends {
        pinType: DesignBoardPinTargetType;
    }
>(
    tile: Record<string, unknown>,
    type: DesignBoardPinTargetType
): tile is T => {
    return 'pinType' in tile && (tile as T).pinType === type;
};

export const getUploadFileTileImage = (
    src: string,
    alt = '',
    objectFit = 'cover'
) => {
    return {
        images: [
            {
                src,
                alt,
                objectFit,
                height: '100%',
                width: '100%',
            },
        ] as TileImageProps['images'],
    } as ShorthandValue<TileImageProps>;
};

export const getPinWebsiteLink = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional?.Website || '';
        case 'Professional':
            return pin.Target.Website || '';
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return '';
    }
};

export const getPinProfessional = (
    pin: Pick<DetailedPinWithChecked, 'Target'>
) => {
    switch (pin.Target.__typename) {
        case 'Project':
        case 'Product':
        case 'Article':
            return pin.Target.Professional;
        case 'Professional':
            return pin.Target;
        case 'MemberUpload':
        case 'LibraryImage':
        default:
            return undefined;
    }
};
